import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Wordmark } from "./Wordmark";
import { ThemeToggle } from "./ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Icons } from "@/lib/icons";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { MobileTabBar } from "./MobileTabBar";
import { useIsAdmin } from "@/hooks/useIsAdmin";

type NavEntry = { to: string; label: string; icon: typeof Icons.dashboard; auth: boolean };
const NAV_MAIN: NavEntry[] = [
  { to: "/dashboard", label: "Dashboard", icon: Icons.dashboard, auth: true },
  { to: "/chat", label: "Talk to Claira", icon: Icons.chat, auth: true },
  { to: "/journal", label: "Journal", icon: Icons.journal, auth: true },
  { to: "/screenings", label: "Screenings", icon: Icons.screenings, auth: false },
  { to: "/resources", label: "Resources", icon: Icons.resources, auth: false },
  { to: "/support", label: "Help & Support", icon: Icons.info, auth: false },
];

// Free wellbeing tools — surfaced in the sidebar so signed-in users can reach them
// from any page (they previously only appeared on the public landing).
const NAV_TOOLS: NavEntry[] = [
  { to: "/triage", label: "Where to start", icon: Icons.brain, auth: false },
  { to: "/mood", label: "Mood check-in", icon: Icons.heart, auth: false },
  { to: "/grounding", label: "Grounding", icon: Icons.shield, auth: false },
  { to: "/learn", label: "Learn", icon: Icons.report, auth: false },
  { to: "/support-directory", label: "Crisis & support", icon: Icons.chat, auth: false },
];

function NavItems({ onNav, collapsed }: { onNav?: () => void; collapsed?: boolean }) {
  const { user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const clippedReveal = collapsed !== undefined;
  const adminItems: NavEntry[] = isAdmin
    ? [{ to: "/admin", label: "Admin", icon: Icons.shield, auth: true }]
    : [];
  const groups: { label: string; items: NavEntry[] }[] = [
    { label: "Main", items: [...NAV_MAIN, ...adminItems].filter((n) => !n.auth || user) },
    { label: "Wellbeing tools", items: NAV_TOOLS.filter((n) => !n.auth || user) },
  ].filter((g) => g.items.length > 0);

  const renderItem = (n: NavEntry) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onNav}
          title={collapsed ? n.label : undefined}
          className={({ isActive }) =>
            cn(
              clippedReveal
                ? "grid h-10 w-full grid-cols-[3rem_minmax(0,1fr)] items-center rounded-xl pr-3 text-sm font-medium transition-colors"
                : "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all",
              isActive
                ? "bg-gradient-brand text-primary-foreground shadow-card"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground"
            )
          }
        >
          <n.icon className={cn("h-4 w-4 shrink-0", clippedReveal && "mx-auto")} />
          <span className={cn("truncate", clippedReveal && "min-w-0 pl-2")}>{n.label}</span>
        </NavLink>
  );

  return (
    <nav className={cn("flex flex-col gap-4", clippedReveal && "w-64 px-2")}>
      {groups.map((g, idx) => (
        <div key={g.label} className="flex flex-col gap-1">
          {idx > 0 && (
            clippedReveal ? (
              <div className="px-3 pb-1 pt-1">
                <div
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-opacity",
                    collapsed ? "opacity-0" : "opacity-100"
                  )}
                >
                  {g.label}
                </div>
              </div>
            ) : (
              <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                {g.label}
              </div>
            )
          )}
          {g.items.map(renderItem)}
        </div>
      ))}
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth();
  const loc = useLocation();
  const isLanding = loc.pathname === "/";
  const isLegal = ["/privacy", "/terms", "/cookie-policy", "/legal", "/security", "/support"].includes(loc.pathname);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Auto-close the mobile drawer whenever the route changes (covers iOS taps).
  useEffect(() => { setMobileNavOpen(false); }, [loc.pathname]);
  // Single source of truth for sidebar UI state.
  // `intent` is what the user wants (pinned OR hovered after debounce).
  // `phase` is the actual visual state, advanced only by transitionend / debounced intent.
  // Disclaimer derives strictly from phase === 'expanded' so it can never race.
  const [pinned, setPinned] = useState(false);
  const [intent, setIntent] = useState<"open" | "closed">("closed");
  const [phase, setPhase] = useState<"collapsed" | "expanding" | "expanded" | "collapsing">("collapsed");
  const hoverTimer = useRef<number | null>(null);

  const wantsOpen = pinned || intent === "open";
  const expanded = phase === "expanding" || phase === "expanded";
  const showDisclaimer = phase === "expanded";

  // Drive phase from intent. Only transitionend (or pin toggle) finalises 'expanded'/'collapsed'.
  useEffect(() => {
    setPhase((prev) => {
      if (wantsOpen && (prev === "collapsed" || prev === "collapsing")) return "expanding";
      if (!wantsOpen && (prev === "expanded" || prev === "expanding")) return "collapsing";
      return prev;
    });
  }, [wantsOpen]);

  const clearHoverTimer = () => {
    if (hoverTimer.current) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };
  useEffect(() => () => clearHoverTimer(), []);

  const handleEnter = () => {
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setIntent("open"), 225);
  };
  const handleLeave = () => {
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setIntent("closed"), 250);
  };

  const onAsideTransitionEnd = useCallback((e: React.TransitionEvent<HTMLElement>) => {
    if (e.propertyName !== "width") return;
    setPhase((prev) => {
      if (prev === "expanding" && wantsOpen) return "expanded";
      if (prev === "collapsing" && !wantsOpen) return "collapsed";
      return prev;
    });
  }, [wantsOpen]);

  if (isLanding) return <>{children}</>;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="app-header sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl safe-top safe-left safe-right">
        <div className="flex h-16 items-center justify-between pr-4 md:pr-6 pl-4 md:pl-0 safe-left safe-right">
          <div className="flex items-center gap-3">
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden"><Icons.menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64">
                <div className="mb-6 mt-2"><Link to={user ? "/dashboard" : "/"}><Wordmark variant="lockup" size="md" /></Link></div>
                <NavItems onNav={() => setMobileNavOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="hidden md:flex w-16 shrink-0 items-center justify-center native-hide">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPinned((p) => !p)}
                aria-label={pinned ? "Collapse sidebar" : "Expand sidebar"}
              >
                <Icons.panel className="h-5 w-5" />
              </Button>
            </div>
            <Link to={user ? "/dashboard" : "/"}><Wordmark variant="lockup" size="lg" /></Link>
          </div>
          <div className="flex items-center gap-2">
            {/* Native-only: gentle reminder accessible from header (sidebar is hidden on native) */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden native-only-inline"
                  aria-label="A gentle reminder"
                >
                  <Icons.info className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-3xl safe-bottom">
                <div className="mx-auto max-w-md py-4">
                  <h2 className="font-medium text-foreground mb-2">A gentle reminder</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Claira is a wellbeing companion, not a medical diagnosis. If you're in crisis, please reach out to local emergency services.
                  </p>
                </div>
              </SheetContent>
            </Sheet>
            <ThemeToggle />
            {user ? (
              <>
                <Button variant="ghost" size="icon" asChild><Link to="/settings"><Icons.settings className="h-5 w-5" /></Link></Button>
                <Button variant="ghost" size="icon" onClick={signOut} aria-label="Sign out"><Icons.signOut className="h-5 w-5" /></Button>
              </>
            ) : (
              <Button asChild variant="default" size="sm" className="bg-gradient-brand"><Link to="/auth"><Icons.signIn className="mr-2 h-4 w-4" /> Sign in</Link></Button>
            )}
          </div>
        </div>
      </header>

      <div className="relative flex">
        {/* Collapsible rail — minimised by default; expands on hover or when pinned.
            When unpinned, the rail is always an overlay (fixed) so the page below
            never reflows during expand/collapse. A permanent w-16 spacer keeps the
            layout stable. When pinned, the rail flows in the layout as a sticky
            column and width changes do affect the content area. */}
        {!pinned && (
          <div
            aria-hidden
            className="hidden md:block shrink-0 w-16 native-hide"
            onMouseEnter={handleEnter}
          />
        )}
        <aside
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onTransitionEnd={onAsideTransitionEnd}
          className={cn(
            "hidden md:flex flex-col border-r border-border/60 py-2 native-hide",
            "transition-[width] duration-500 ease-out bg-background/80 backdrop-blur-xl",
            "h-[calc(100dvh-4rem)] overflow-y-auto overflow-x-hidden",
            expanded ? "w-64" : "w-16",
            pinned
              ? "sticky top-16 self-start shrink-0"
              : cn(
                  "fixed left-0 top-16 z-30",
                  expanded && "shadow-soft"
                )
          )}
        >
          <NavItems collapsed={!expanded} />
          {showDisclaimer && (
            <div className="mt-auto rounded-2xl surface-soft p-4 text-xs text-muted-foreground leading-relaxed animate-fade-in">
              <p className="font-medium text-foreground mb-1">A gentle reminder</p>
              Claira is a wellbeing companion, not a medical diagnosis. If you're in crisis, please reach out to local emergency services.
            </div>
          )}
        </aside>
        <main className="flex-1 min-w-0 px-4 md:px-8 py-6 md:py-10">
          <div key={loc.pathname} className="mx-auto w-full max-w-5xl animate-reveal-x">
            {!isLegal && (
              <div className="mb-4 flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
                <Icons.phone className="h-3 w-3" />
                In crisis?{" "}
                <a href="tel:116123" className="font-medium text-warm underline underline-offset-2 hover:no-underline">
                  Call Samaritans 116 123
                </a>
              </div>
            )}
            {children}
            <footer className="mt-16 border-t border-border/60 pt-8 pb-4 text-center text-xs text-muted-foreground">
              <Wordmark size="sm" />
              <p className="mt-3">© {new Date().getFullYear()} Claira. Clarity. Care. Connection.</p>
              <p className="mt-1 opacity-80">Not a medical device. For wellbeing support only.</p>
              <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1">
                <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
                <Link to="/terms" className="hover:text-foreground">Terms</Link>
                <Link to="/cookie-policy" className="hover:text-foreground">Cookies</Link>
                <Link to="/legal" className="hover:text-foreground">Legal</Link>
                <Link to="/security" className="hover:text-foreground">Security</Link>
                <Link to="/support" className="hover:text-foreground">Support</Link>
                <a href="mailto:help@claira.uk" className="hover:text-foreground">help@claira.uk</a>
              </div>
              <p className="mt-2 opacity-60">Registered in England and Wales · ICO registration: [to be inserted]</p>
            </footer>
          </div>
        </main>
      </div>
      <MobileTabBar />
    </div>
  );
}
